import type {Content, GroupField, ImageField, RichTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import cn from 'classnames'
import type {ComparisonSectionSliceDefaultPrimary, Simplify} from 'prismicio-types'
import {Icon} from 'src/common/Icon'
import {InfoTooltip} from 'src/common/InfoTooltip'
import {AvailabilityIcon} from './AvailabilityIcon'

export const ComparisonTableHead = ({
  comparisonProducts,
  backgroundColor,
}: {
  comparisonProducts: ComparisonSectionSliceDefaultPrimary['comparison_products']
  backgroundColor: ComparisonSectionSliceDefaultPrimary['background_color']
}) => (
  <div
    className={cn(
      'z-10 flex gap-5 rounded-t-xl p-5 max-md:sticky max-md:top-[64px] md:gap-6 md:rounded-t-2xl md:px-12 md:pb-8 md:pt-12',
      {
        'bg-beige-200': backgroundColor === 'Moss White' || !isFilled.select(backgroundColor),
        'bg-moss-white': backgroundColor !== 'Moss White',
      },
    )}
  >
    <div className="w-full p-0 max-md:hidden" />
    {comparisonProducts.map((product, index) => (
      <ComparisonTableHeaderCell key={index} product={product} index={index} />
    ))}
  </div>
)

const ComparisonTableHeaderCell = ({
  product,
  index,
}: {
  product: ComparisonSectionSliceDefaultPrimary['comparison_products'][0]
  index: number
}) => {
  if (product === undefined) {
    return null
  }

  return (
    <div key={index} className="flex w-full flex-col gap-2 p-0">
      {isFilled.image(product.image) && <ImageWrapper image={product.image} disableWrapper={product.disable_wrapper} />}
      <div className="flex w-full flex-col gap-1 text-start">
        <h4 className="text-h4 text-moss-black">{product.title}</h4>
        <span className="text-body-s font-normal text-grey-300">{product.subtext}</span>
      </div>
    </div>
  )
}

const ImageWrapper = ({image, disableWrapper = false}: {image: ImageField; disableWrapper?: boolean}) => {
  if (!disableWrapper) {
    return (
      <div className="relative flex size-10 items-center justify-center rounded-[4px] bg-moss-grey-100 p-2">
        <PrismicNextImage field={image} className="size-6" />
        <div className="pointer-events-none absolute inset-0 rounded-[4px] border border-black/[10%]" />
      </div>
    )
  }

  return <PrismicNextImage field={image} className="size-10 rounded-[4px]" />
}

type ComparisonTableBodyProps = {
  tableBody: GroupField<Simplify<Content.ComparisonSectionSliceDefaultPrimaryTableItem>>
  backgroundColor: ComparisonSectionSliceDefaultPrimary['background_color']
  contentCenter?: boolean
  enableReskin?: boolean
}

export const ComparisonTableBody = ({
  tableBody,
  backgroundColor,
  contentCenter = false,
  enableReskin = false,
}: ComparisonTableBodyProps) => (
  <div>
    {tableBody.map((row, index) => (
      <TableRow
        key={index}
        row={row}
        index={index}
        backgroundColor={backgroundColor}
        contentCenter={contentCenter}
        enableReskin={enableReskin}
      />
    ))}
  </div>
)

const TooltipContent = ({tooltipMessage}: {tooltipMessage: RichTextField}) => (
  <PrismicRichText
    field={tooltipMessage}
    components={{
      paragraph: ({children}) => <p className="text-body-s text-moss-white">{children}</p>,
      heading6: ({children}) => <p className="text-body-xxs px-2 pb-2 pt-3 uppercase text-grey-150">{children}</p>,
      list: ({children}) => <ul className="flex flex-col gap-2 px-2 pb-3">{children}</ul>,
      listItem: ({children}) => (
        <li className="text-body-bold-s flex items-center gap-2 align-middle text-moss-white">
          <Icon name="check" className="size-4 min-w-4" />
          {children}
        </li>
      ),
    }}
  />
)

const TableCell = ({children, textCenter = false}: {children: React.ReactNode; textCenter?: boolean}) => (
  <div
    className={cn('flex w-full items-center gap-3 px-0 py-5 md:py-[22px]', {
      'justify-center': textCenter,
    })}
  >
    {children}
  </div>
)

type TableRowProps = {
  row: Simplify<Content.ComparisonSectionSliceDefaultPrimaryTableItem>
  index: number
  backgroundColor: ComparisonSectionSliceDefaultPrimary['background_color']
  contentCenter?: boolean
  enableReskin?: boolean
}

const TableRow = ({row, index, backgroundColor, contentCenter = false}: TableRowProps) => (
  <div
    key={index}
    className={cn('grid grid-cols-2 gap-5 gap-y-0 px-5 md:grid-cols-3 md:gap-6 md:px-12', {
      'bg-moss-white': index % 2 === 0 && backgroundColor !== 'Moss White',
      'bg-moss-grey-100':
        (index % 2 === 0 && (backgroundColor === 'Moss White' || !isFilled.select(backgroundColor))) || index % 2 !== 0,
    })}
  >
    <div className="text-tg-body-m w-full pt-[18px] text-moss-black max-md:col-span-full md:px-0 md:py-[22px]">
      <div className="flex items-center gap-1.5">
        <span className="w-full">{row.benefit_header}</span>
        {isFilled.richText(row.tooltip_message) && (
          <InfoTooltip
            id={`${index}-${row.benefit_header?.replace(/[^\w\s]/g, '').replace(/ /g, '_')}`}
            className="self-end pb-[3.5px]"
          >
            <TooltipContent tooltipMessage={row.tooltip_message} />
          </InfoTooltip>
        )}
      </div>
    </div>
    <TableCell textCenter={contentCenter}>
      <AvailabilityIcon available={row.available_in_product_1} />
      <span className="text-body-s font-normal text-grey-450">{row.note_product_1}</span>
    </TableCell>
    <TableCell textCenter={contentCenter}>
      <AvailabilityIcon available={row.available_in_product_2} />
      <span className="text-body-s font-normal text-grey-450">{row.note_product_2}</span>
    </TableCell>
  </div>
)
